import type { User } from "@fusioncraft/shared";
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";

type Mode = "login" | "register";

export function AuthScreen({ onAuthed }: { onAuthed: (u: User) => void }) {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  // Hide "Create account" entirely when the server has sign-ups closed.
  const [signupOpen, setSignupOpen] = useState(false);
  useEffect(() => {
    api.signupStatus().then((s) => setSignupOpen(s.open), () => setSignupOpen(false));
  }, []);
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } =
        mode === "register"
          ? await api.register(email, password, inviteCode)
          : await api.login(email, password, needsCode ? code : undefined);
      onAuthed(user);
    } catch (err) {
      if (err instanceof ApiError && err.code === "totp_required") {
        setNeedsCode(true);
      } else {
        setError((err as Error).message);
        if (err instanceof ApiError && err.code === "totp_invalid") setCode("");
      }
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setNeedsCode(false);
    setCode("");
  }

  return (
    <main className="auth">
      <section className="auth-card">
        <form className="auth-form" onSubmit={submit}>
          {signupOpen && (
            <div className="segmented" role="tablist">
              <button type="button" role="tab" aria-selected={mode === "login"} onClick={() => switchMode("login")}>
                Sign in
              </button>
              <button type="button" role="tab" aria-selected={mode === "register"} onClick={() => switchMode("register")}>
                Create account
              </button>
            </div>
          )}

          {needsCode ? (
            <>
              <p className="form-note">
                Two-factor authentication is on for <strong>{email}</strong>. Enter the 6-digit code from your
                authenticator app.
              </p>
              <label className="field">
                <span>Authentication code</span>
                <input
                  className="input input-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]*"
                  maxLength={7}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </label>
            </>
          ) : (
            <>
              <label className="field">
                <span>Email</span>
                <input
                  className="input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </label>
              <label className="field">
                <span>Password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  minLength={mode === "register" ? 10 : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {mode === "register" && <small>At least 10 characters.</small>}
              </label>
              {mode === "register" && (
                <label className="field">
                  <span>Invite code</span>
                  <input
                    className="input"
                    autoComplete="off"
                    spellCheck={false}
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    required
                  />
                  <small>Accounts are invite-only.</small>
                </label>
              )}
            </>
          )}

          {error && <p className="form-error" role="alert">{error}</p>}

          <button className="btn btn-primary btn-wide" disabled={busy}>
            {busy ? "One moment…" : needsCode ? "Verify" : mode === "login" ? "Sign in" : "Create account"}
          </button>
          {needsCode && (
            <button type="button" className="btn-link" onClick={() => switchMode("login")}>
              Use a different account
            </button>
          )}
        </form>
      </section>
    </main>
  );
}
